import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Authorizer } from '../../../models/AuthorizerModel';
import showToast from '../../../utils/toast';
import AuthorizersTable from './AuthorizersTable';
import AuthorizerForm from '../../../components/Companies/AuthorizersForm';
import ButtonCancelForm from '../../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../../components/Base/ConfirmAlert';
import useAuthorizersService from '../../../services/useAuthorizersService';
import useCompaniesService from '../../../services/useCompaniesService';
import { useFormErrors } from '../../../hooks/useFormErrors';
import TableTextSearch from '../../../components/Base/TableTextSearch';
import Pagination from '../../../components/Base/Pagination';
import MaintainerPageShell from '../../../components/Base/MaintainerPageShell';
import { Plus, ShieldCheck } from 'lucide-react';

const AuthorizersPage: React.FC = () => {
  const svc = useAuthorizersService();
  const { list: getAuthorizers, store: createAuthorizer, update: updateAuthorizer, remove: removeAuthorizer, fetching } = svc;
  const companiesSvc = useCompaniesService();
  const { list: listCompanies } = companiesSvc;

  const [authorizers, setAuthorizers] = useState<Authorizer[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Authorizer | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [authorizerIdToDelete, setAuthorizerIdToDelete] = useState<number | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });

  const loadCompanies = useCallback(() => {
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    listCompanies({}, {
      onSuccess: (resp: any) => {
        const items = resp?.data.companies ?? [];
        const mapped = items.map((c: any) => ({
          value: c.id,
          label: c.name ?? c.company_name ?? 'Sin nombre',
        }));
        setCompanies(mapped);
      },
      onError: () => showToast('Error cargando empresas', 'error'),
    }, ctrl.signal);
  }, [listCompanies]);

  const loadAuthorizers = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    getAuthorizers(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.authorizers) ? data.authorizers : data.items ?? [];
          setAuthorizers(items);
          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp) => {
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'Error cargando autorizadores', 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [getAuthorizers]);

  useEffect(() => {
    loadAuthorizers();
    loadCompanies();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadAuthorizers, loadCompanies]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadAuthorizers(1, value), 400);
    },
    [loadAuthorizers]
  );

  const openDeleteConfirmation = (id: number) => {
    setAuthorizerIdToDelete(id);
    setIsAlertOpen(true);
  };

  const openCreate = () => {
    svc.create({
      onSuccess: (resp: any) => {
        const list = resp?.data ?? [];
        const mapped = list.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(mapped);

        setEditing({
          name: '',
          first_lastname: '',
          second_lastname: '',
          last_name: '',
          phone_number: '',
          email: '',
          company: undefined,
        });

        setShowForm(true);
      },
      onError: () => showToast('Error cargando formulario', 'error'),
    });
  };

  const openEdit = (a: Authorizer) => {
    svc.edit(a.id!, {
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        const authorizer = data.authorizer ?? {};
        const companiesList = data.companies ?? [];

        const normalized = {
          ...authorizer,
          company: authorizer.company_id ?? authorizer.company ?? '',
        };

        setEditing(normalized);
        setCompanies(companiesList.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        })));
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del autorizador', 'error'),
    });
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);

    try {
      const payload = {
        name: editing.name,
        first_lastname: editing.first_lastname,
        company: editing.company,
        phone_number: (editing.phone_number ?? '').trim(),
        email: (editing.email ?? '').trim(),
      };

      if (editing.id) {
        const r = await updateAuthorizer(editing.id, payload);
        return r;
      }
      const r = await createAuthorizer(payload);
      return r;
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      loadAuthorizers(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }

    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const handleConfirmDelete = () => {
    if (authorizerIdToDelete === null) return;

    removeAuthorizer(authorizerIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        loadAuthorizers(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setAuthorizerIdToDelete(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setAuthorizerIdToDelete(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || authorizers.length}
        </span>
        autorizadores
      </span>
    );

  return (
    <MaintainerPageShell
      title="Autorizadores"
      icon={<ShieldCheck size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo autorizador
        </button>
      }
    >
      <AuthorizersTable
        data={authorizers}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre, apellido o correo..."
            aria-label="Buscar autorizadores"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                loadAuthorizers(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="companies-modal">
          <div className="companies-modal-inner">
            <h3>{editing.id ? 'Editar autorizador' : 'Crear autorizador'}</h3>
            <AuthorizerForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              companies={companies}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar autorizador?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default AuthorizersPage;
